import { Injectable, inject } from '@angular/core';
import { FirebaseService } from './firebase.service.js';
import type {
  DashboardKPI, AdminUserDetail, UserActivity, AuditLogResponse, AuditFilters,
  HealthReport, BannerPayload, UsersListResponse,
  QuotaOverrideRequest, QuotaDefaultsPatchRequest, QuotaDefaultsDoc,
} from '../models/admin.types.js';
import { doc, getDoc } from 'firebase/firestore';

// Thrown when auth-rf returns 403 — caller should close the admin panel and
// alert the operator that admin rights were revoked.
export class AdminForbiddenError extends Error {
  constructor() { super('admin access denied (403)'); this.name = 'AdminForbiddenError'; }
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private firebase = inject(FirebaseService);

  private get base(): string {
    return this.firebase.authRfUrl;
  }

  private async authedFetch<T = any>(path: string, init: RequestInit = {}): Promise<T> {
    const user = this.firebase.auth?.currentUser;
    if (!user) throw new Error('not authenticated');
    const idToken = await user.getIdToken();
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
      ...((init.headers as Record<string, string>) || {}),
    };
    const resp = await fetch(`${this.base}${path}`, { ...init, headers });
    if (resp.status === 403) throw new AdminForbiddenError();
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      throw new Error(`HTTP ${resp.status}: ${body || resp.statusText}`);
    }
    return resp.json() as Promise<T>;
  }

  getDashboard(): Promise<DashboardKPI> {
    return this.authedFetch<DashboardKPI>('/admin/dashboard');
  }

  searchUsers(q: string = '', limit: number = 50, offset: number = 0): Promise<UsersListResponse> {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this.authedFetch<UsersListResponse>(`/admin/users?${params.toString()}`);
  }

  getUser(uid: string): Promise<AdminUserDetail> {
    return this.authedFetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(uid)}`);
  }

  getUserActivity(uid: string, n: number = 20): Promise<UserActivity> {
    return this.authedFetch<UserActivity>(`/admin/users/${encodeURIComponent(uid)}/activity?n=${n}`);
  }

  setPro(uid: string, isPro: boolean) {
    return this.authedFetch(`/admin/users/${encodeURIComponent(uid)}/pro`, {
      method: 'PATCH',
      body: JSON.stringify({ isPro }),
    });
  }

  setRole(uid: string, role: 'user' | 'admin') {
    return this.authedFetch(`/admin/users/${encodeURIComponent(uid)}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  refreshClaims(uid: string) {
    return this.authedFetch(`/admin/users/${encodeURIComponent(uid)}/refresh-claims`, {
      method: 'POST',
    });
  }

  softDeleteUser(uid: string) {
    return this.authedFetch(`/admin/users/${encodeURIComponent(uid)}`, {
      method: 'DELETE',
    });
  }

  getAuditLog(filters: AuditFilters = {}): Promise<AuditLogResponse> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    }
    return this.authedFetch<AuditLogResponse>(`/admin/audit-log?${params.toString()}`);
  }

  getHealth(): Promise<HealthReport> {
    return this.authedFetch<HealthReport>('/admin/health');
  }

  setBanner(payload: BannerPayload) {
    return this.authedFetch('/admin/broadcast', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  clearBanner() {
    return this.authedFetch('/admin/broadcast', {
      method: 'POST',
      body: JSON.stringify({ message: null }),
    });
  }

  // ── Daily-quota admin actions ──────────────────────────────────────────
  resetUserQuota(uid: string, reason: string) {
    return this.authedFetch(`/admin/users/${encodeURIComponent(uid)}/quota/reset`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  setUserQuotaOverride(uid: string, body: QuotaOverrideRequest) {
    return this.authedFetch(`/admin/users/${encodeURIComponent(uid)}/quota/override`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  setQuotaDefaults(body: QuotaDefaultsPatchRequest) {
    return this.authedFetch<{ ok: boolean; defaults: QuotaDefaultsDoc; propagation_seconds: number }>(
      '/admin/quota-defaults',
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  }

  /**
   * Read current global defaults straight from Firestore (rules: authed-only).
   * No auth-rf round-trip. Used by the «Глобальные дефолты» admin form to
   * prefill the inputs.
   */
  async getQuotaDefaults(): Promise<QuotaDefaultsDoc | null> {
    const fs = this.firebase.firestore;
    if (!fs) return null;
    const snap = await getDoc(doc(fs, 'system', 'quota-defaults'));
    return snap.exists() ? (snap.data() as QuotaDefaultsDoc) : null;
  }
}
